import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Progress,
  Badge,
  Alert,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  PhoneOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";
import { mockRABills } from "./mockRABills";

const { Option } = Select;
const { RangePicker } = DatePicker;

const RABillsOverdue: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [overdueRABills, setOverdueRABills] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    severityLevel: "",
    contractor: "",
    raBillNumber: "",
    woNumber: "",
    dateRange: null as any,
    site: "",
    overdueRange: "",
  });

  useEffect(() => {
    loadOverdueRABills();
  }, []);

  const loadOverdueRABills = () => {
    setLoading(true);
    try {
      // Filter RA Bills that are overdue (past expected payment date)
      const currentDate = dayjs();
      const overdueBills = mockRABills
        .filter(
          (bill) =>
            ["Approved", "Posted"].includes(bill.status) &&
            bill.balanceAmount > 0
        )
        .map((bill) => {
          const approvalDate = dayjs(bill.approvedOn);
          const expectedPaymentDate = approvalDate.add(30, "days"); // 30 days payment term
          const overdueBy = currentDate.diff(expectedPaymentDate, "days");

          return {
            ...bill,
            approvalDate: bill.approvedOn,
            expectedPaymentDate: expectedPaymentDate.format("YYYY-MM-DD"),
            overdueBy: Math.max(0, overdueBy),
            isOverdue: overdueBy > 0,
            severityLevel:
              overdueBy > 60
                ? "Critical"
                : overdueBy > 30
                ? "High"
                : overdueBy > 0
                ? "Medium"
                : "Low",
            dueDate: expectedPaymentDate.format("YYYY-MM-DD"),
          };
        })
        .filter((bill) => bill.isOverdue)
        .sort((a, b) => b.overdueBy - a.overdueBy); // Sort by most overdue first

      setOverdueRABills(overdueBills);
    } catch (error) {
      console.error("Error loading overdue RA Bills:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string): string => {
    const severityColors: { [key: string]: string } = {
      Critical: "#ff4d4f",
      High: "#fa8c16",
      Medium: "#faad14",
      Low: "#52c41a",
    };
    return severityColors[severity] || "#d9d9d9";
  };

  const getOverdueColor = (days: number): string => {
    if (days > 60) return "#ff4d4f";
    if (days > 30) return "#fa8c16";
    if (days > 0) return "#faad14";
    return "#52c41a";
  };

  const getPaymentProgress = (
    paidAmount: number,
    totalAmount: number
  ): number => {
    if (totalAmount === 0) return 0;
    return Math.round((paidAmount / totalAmount) * 100);
  };

  const handleViewRABill = (record: any) => {
    navigate(
      `${process.env.PUBLIC_URL}/ra-bills-view-details/${record.raBillId}?mode=view`
    );
  };

  const handleEscalate = (record: any) => {
    // Implementation for escalation (send to management, etc.)
    console.log("Escalate:", record.raBillNumber);
  };

  const handleSendReminder = (record: any) => {
    // Implementation for sending payment reminder
    console.log("Send reminder for:", record.raBillNumber);
  };

  const resetFilters = () => {
    setFilters({
      severityLevel: "",
      contractor: "",
      raBillNumber: "",
      woNumber: "",
      dateRange: null,
      site: "",
      overdueRange: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const totalOverdue = overdueRABills.length;
  const totalOverdueValue = overdueRABills.reduce(
    (sum, bill) => sum + bill.balanceAmount,
    0
  );
  const criticalCount = overdueRABills.filter(
    (bill) => bill.severityLevel === "Critical"
  ).length;
  const avgOverdueDays =
    overdueRABills.length > 0
      ? Math.round(
          overdueRABills.reduce((sum, bill) => sum + bill.overdueBy, 0) /
            overdueRABills.length
        )
      : 0;

  const columns: TableColumnsType<any> = [
    {
      title: "RA Bill #",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 180,
      fixed: "left",
      render: (text: string, record: any) => (
        <div>
          <span
            className="inlineTableCellLink"
            id={`raBillOverdue-${record?.raBillId}`}
            onClick={() => handleViewRABill(record)}
          >
            {text}
          </span>
          {/* <div className="mt-1">
            <Tag color={getSeverityColor(record.severityLevel)}>
              {record?.severityLevel?.toUpperCase()}
            </Tag>
          </div> */}
        </div>
      ),
    },
    {
      title: "WO #",
      key: "workOrderNumber",
      dataIndex: "workOrderNumber",
      width: 200,
      // render: (_, record) => (
      //   <div>
      //     <div className="fw-bold text-primary">{record.workOrderNumber}</div>
      //     <div className="text-muted small mt-1">{record.contractor}</div>
      //   </div>
      // ),
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 180,
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName}</div>
        </div>
      ),
    },
    {
      title: "Outstanding Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 150,
      align: "right",
      render: (balance, record) => (
        <div>
          <div className="fw-bold text-danger">
            {formatCurrency(record.balanceAmount)}
          </div>
          <div className="text-muted small">
            of {formatCurrency(record.grossTotal)}
          </div>
        </div>
      ),
      sorter: (a, b) => a.balanceAmount - b.balanceAmount,
    },
    {
      title: "Due Date",
      key: "paymentDueDate",
      width: 130,
      render: (_, record) => (
        <div>
          <div className="fw-bold text-danger">
            {dayjs(record.paymentDueDate).format("DD-MMM-YYYY")}
          </div>
          <div className="text-muted small">
            {dayjs(record.updatedAt).format("DD-MMM-YYYY")} (Approved)
          </div>
        </div>
      ),
      sorter: (a, b) =>
        dayjs(a.expectedPaymentDate).unix() -
        dayjs(b.expectedPaymentDate).unix(),
    },
    {
      title: "Overdue By",
      key: "slaAgingForDue",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div>
          <Badge
            count={`${record.slaAgingForDue}d`}
            color={getOverdueColor(record.slaAgingForDue)}
            style={{ fontSize: "12px" }}
          />
          <div className="text-muted small mt-1">
            {record.slaAgingForDue > 60
              ? "Critical"
              : record.slaAgingForDue > 30
              ? "High Risk"
              : "At Risk"}
          </div>
        </div>
      ),
      sorter: (a, b) => b.slaAgingForDue - a.slaAgingForDue,
    },
    {
      title: "Payment Progress",
      key: "progress",
      width: 140,
      align: "center",
      render: (_, record) => {
        const progress = getPaymentProgress(
          record.paidAmount,
          record.grossTotal
        );
        return (
          <div>
            <Progress
              percent={progress}
              size="small"
              strokeColor={progress > 0 ? "#52c41a" : "#ff4d4f"}
              showInfo={false}
            />
            <div className="text-muted small mt-1">
              {formatCurrency(record.paidAmount)} paid
            </div>
          </div>
        );
      },
    },
    {
      title: "Last Contact",
      key: "lastContactDate",
      width: 130,
      render: (_, record) => (
        <div>
          {record.lastContactDate ? (
            <>
              <div className="fw-bold">
                {dayjs(record.lastContactDate).format("DD-MMM-YYYY")}
              </div>
              <div className="text-muted small">Payment</div>
            </>
          ) : (
            <>
              <div className="text-muted">No Contact</div>
              <div className="text-danger small">⚠️ Action Needed</div>
            </>
          )}
        </div>
      ),
    },
    {
      title: "Risk Level",
      key: "riskLevel",
      width: 120,
      render: (_, record) => {
        const riskLevel =
          record.slaAgingForDue > 90
            ? "Very High"
            : record.slaAgingForDue > 60
            ? "High"
            : record.slaAgingForDue > 30
            ? "Medium"
            : "Low";
        const color =
          record.slaAgingForDue > 90
            ? "#ff4d4f"
            : record.slaAgingForDue > 60
            ? "#fa8c16"
            : record.slaAgingForDue > 30
            ? "#faad14"
            : "#52c41a";

        return (
          <div className="text-center">
            <Tag color={color}>{riskLevel}</Tag>
            {record.slaAgingForDue > 60 && (
              <div className="text-danger small mt-1">
                <ExclamationCircleOutlined /> Escalate
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space wrap size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewRABill(record)}
          >
            View
          </Button>
          {/* <Button
            size="small"
            icon={<MailOutlined />}
            onClick={() => handleSendReminder(record)}
          >
            Remind
          </Button>
          {record.overdueBy > 30 && (
            <Button
              size="small"
              danger
              icon={<ExclamationCircleOutlined />}
              onClick={() => handleEscalate(record)}
            >
              Escalate
            </Button>
          )} */}
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Critical Alert Banner */}
      {/* {criticalCount > 0 && (
        <Alert
          type="error"
          showIcon
          message={`Critical Alert: ${criticalCount} bills are critically overdue (60+ days)`}
          description="Immediate action required for payment collection and risk mitigation"
          style={{ marginBottom: 16 }}
          closable
        />
      )} */}

      <TransactionFlowDefaultTemplate
        moduleKey="ra-bills-overdue"
        title="Overdue RA Bills"
        subtitle="Track and escalate overdue Running Account Bills requiring immediate attention"
        columns={columns}
        loading={loading}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <RABillAside updateSm={setSm} sm={sm} />
          </div>
        }
        extra={{
          tableScrollX: 1900,
          showSearch: true,
          searchPlaceholder: "Search by RA Bill#, WO#, or Contractor...",
          kpiWidgets: [
            {
              title: "Total Overdue Bills",
              value: totalOverdue,
              icon: <ExclamationCircleOutlined />,
              color: "#ff4d4f",
            },
            {
              title: "Overdue Value",
              value: formatCurrency(totalOverdueValue),
              icon: <DollarOutlined />,
              color: "#ff4d4f",
            },
            {
              title: "Critical Risk Bills",
              value: criticalCount,
              icon: <ClockCircleOutlined />,
              color: "#ff4d4f",
            },
            {
              title: "Avg Overdue Days",
              value: `${avgOverdueDays} days`,
              icon: <CalendarOutlined />,
              color: "#fa8c16",
            },
          ],
          advancedFilter: {
            visible: drawerVisible,
            onClose: () => setDrawerVisible(false),
            onOpen: () => setDrawerVisible(true),
            content: (
              <div className="p-4">
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <label className="form-label">Severity Level</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select Severity Level"
                      value={filters.severityLevel}
                      onChange={(value) =>
                        setFilters({ ...filters, severityLevel: value })
                      }
                    >
                      <Option value="Critical">Critical (60+ days)</Option>
                      <Option value="High">High (30-60 days)</Option>
                      <Option value="Medium">Medium (1-30 days)</Option>
                    </Select>
                  </Col>
                  <Col span={24}>
                    <label className="form-label">Contractor</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select Contractor"
                      value={filters.contractor}
                      onChange={(value) =>
                        setFilters({ ...filters, contractor: value })
                      }
                    >
                      <Option value="ABC Infra Ltd">ABC Infra Ltd</Option>
                      <Option value="XYZ Constructions">
                        XYZ Constructions
                      </Option>
                      <Option value="BuildTech Pvt Ltd">
                        BuildTech Pvt Ltd
                      </Option>
                    </Select>
                  </Col>
                  <Col span={24}>
                    <label className="form-label">RA Bill #</label>
                    <Input
                      placeholder="Enter RA Bill Number"
                      value={filters.raBillNumber}
                      onChange={(e) =>
                        setFilters({ ...filters, raBillNumber: e.target.value })
                      }
                      allowClear
                    />
                  </Col>
                  <Col span={24}>
                    <label className="form-label">Work Order #</label>
                    <Input
                      placeholder="Enter WO Number"
                      value={filters.woNumber}
                      onChange={(e) =>
                        setFilters({ ...filters, woNumber: e.target.value })
                      }
                      allowClear
                    />
                  </Col>
                  <Col span={24}>
                    <label className="form-label">Site</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select Site"
                      value={filters.site}
                      onChange={(value) =>
                        setFilters({ ...filters, site: value })
                      }
                    >
                      <Option value="Urban Rise - Tower A">
                        Urban Rise - Tower A
                      </Option>
                      <Option value="Urban Rise - Tower B">
                        Urban Rise - Tower B
                      </Option>
                      <Option value="Urban Rise - Tower C">
                        Urban Rise - Tower C
                      </Option>
                    </Select>
                  </Col>
                  <Col span={24}>
                    <label className="form-label">Overdue Range</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select Overdue Range"
                      value={filters.overdueRange}
                      onChange={(value) =>
                        setFilters({ ...filters, overdueRange: value })
                      }
                    >
                      <Option value="1-30">1-30 days overdue</Option>
                      <Option value="31-60">31-60 days overdue</Option>
                      <Option value="61-90">61-90 days overdue</Option>
                      <Option value="90+">90+ days overdue</Option>
                    </Select>
                  </Col>
                  <Col span={24}>
                    <label className="form-label">Due Date Range</label>
                    <RangePicker
                      style={{ width: "100%" }}
                      value={filters.dateRange}
                      onChange={(dates) =>
                        setFilters({ ...filters, dateRange: dates })
                      }
                      placeholder={["From Date", "To Date"]}
                    />
                  </Col>
                </Row>
                <div className="mt-4 d-flex gap-2">
                  <Button
                    type="primary"
                    onClick={applyFilters}
                    style={{ flex: 1 }}
                  >
                    Apply Filters
                  </Button>
                  <Button onClick={resetFilters} style={{ flex: 1 }}>
                    Reset
                  </Button>
                </div>
              </div>
            ),
          },
        }}
      />
    </>
  );
};

export default RABillsOverdue;
